import { Injectable } from '@angular/core';

export interface TransactionHistoryEntry {
  transactionId: string;
  restaurantName: string;
  tableNumber: number;
  date: string; // ISO string
}

const STORAGE_KEY = 'tabify_transaction_history';
const MAX_ENTRIES = 20;
const ENTRY_TTL_MS = 60 * 60 * 1000; // 60 minutes

@Injectable({ providedIn: 'root' })
export class TransactionHistoryService {
  /**
   * Read the history, filter out entries older than ENTRY_TTL_MS, and persist
   * the cleaned list so storage doesn't grow stale. Returns sorted (newest first).
   */
  getHistory(): TransactionHistoryEntry[] {
    if (typeof window === 'undefined') return [];

    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (!stored) return [];

      const entries: TransactionHistoryEntry[] = JSON.parse(stored);
      const now = Date.now();
      const fresh = entries.filter(
        (e) => now - new Date(e.date).getTime() <= ENTRY_TTL_MS,
      );

      // Persist the filtered list if any entries expired so the next read is cheap
      // and other consumers (e.g. computed signals) don't see stale data.
      if (fresh.length !== entries.length) {
        if (fresh.length === 0) {
          localStorage.removeItem(STORAGE_KEY);
        } else {
          localStorage.setItem(STORAGE_KEY, JSON.stringify(fresh));
        }
      }

      return fresh.sort(
        (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime(),
      );
    } catch {
      return [];
    }
  }

  addEntry(entry: TransactionHistoryEntry): void {
    if (typeof window === 'undefined') return;

    const entries = this.getHistory();

    // Remove existing entry with same transactionId (deduplicate)
    const filtered = entries.filter(
      (e) => e.transactionId !== entry.transactionId,
    );

    // Add new entry at the beginning
    filtered.unshift(entry);

    // Cap at max entries
    const capped = filtered.slice(0, MAX_ENTRIES);

    localStorage.setItem(STORAGE_KEY, JSON.stringify(capped));
  }

  /**
   * Remove all stored transaction history entries.
   */
  clear(): void {
    if (typeof window === 'undefined') return;
    localStorage.removeItem(STORAGE_KEY);
  }
}
